import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { useLang } from './i18n.js';

/**
 * "Nytt för dig" (owner 2026-10-30): what is new for the person's roles (the API reads releases.ts). The start-up popup
 * shows the entries; <NewMark k="…" /> beside a button shows a small dot until the person uses it, and highlights the
 * button when "Show me" opened the page (?nytt=key).
 */
export interface ReleaseView {
  key: string;
  date: string;
  title: string;
  text: string;
  show: { path: string; label: string | null } | null;
  /** admins get every role's entries: the roles this one is for */
  forRoles?: string;
}
export interface ReleasesView {
  items: ReleaseView[];
  more: number;
  dots: string[];
  /** the guided tours this person has seen (tour.tsx) */
  tours?: string[];
}

let api: Api | null = null;
let loaded: Promise<ReleasesView> | null = null;
let dots = new Set<string>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((f) => f());

/** Read once per page load (App sets the API when signed in). */
export function loadReleases(a: Api): Promise<ReleasesView> {
  if (api !== a) loaded = null;
  api = a;
  loaded ??= a.get<ReleasesView>('/api/releases').then(
    (v) => {
      dots = new Set(v.dots);
      changed();
      return v;
    },
    () => ({ items: [], more: 0, dots: [] })
  );
  return loaded;
}

/** Seen in the popup (Got it), or the feature used (tried: the dot goes away too). */
export async function markReleases(a: Api, keys: string[], tried = false): Promise<void> {
  if (!keys.length) return;
  if (tried) {
    for (const k of keys) dots.delete(k);
    changed();
  }
  await a.post('/api/releases/seen', { keys, tried });
}

/** The key "Show me" opened this page for, if any. */
export const spotKey = (search = typeof location === 'undefined' ? '' : location.search): string | null => new URLSearchParams(search).get('nytt');

/**
 * Put next to (inside) the new button: a dot while the feature is new to me; a click on the button marks it used.
 * When "Show me" brought me here the button is highlighted and scrolled into view.
 */
export function NewMark(p: { k: string }) {
  const label = useLang() === 'sv' ? 'Nytt' : 'New';
  const ref = useRef<HTMLSpanElement>(null);
  const [, setRev] = useState(0);
  useEffect(() => {
    const f = () => setRev((n) => n + 1);
    listeners.add(f);
    if (api) void loadReleases(api);
    return () => void listeners.delete(f);
  }, []);
  useEffect(() => {
    const host = ref.current?.closest('button, a') as HTMLElement | null;
    if (!host) return;
    const used = () => {
      if (api && dots.has(p.k)) void markReleases(api, [p.k], true).catch(() => undefined);
    };
    host.addEventListener('click', used);
    let t: ReturnType<typeof setTimeout> | undefined;
    if (spotKey() === p.k) {
      host.classList.add('new-spot');
      host.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      t = setTimeout(() => host.classList.remove('new-spot'), 6000);
    }
    return () => {
      host.removeEventListener('click', used);
      if (t) clearTimeout(t);
    };
  }, [p.k]);
  const on = dots.has(p.k);
  return <span ref={ref} class={on ? 'new-dot' : 'new-dot-off'} data-new={p.k} role={on ? 'img' : undefined} aria-label={on ? label : undefined} title={on ? label : undefined} />;
}
